export const UINT32_MAX = 0xffff_ffff;
export const UINT32_RANGE = 0x1_0000_0000;

export interface FixedPointXZ {
  readonly x: number;
  readonly z: number;
}

export interface RunScopedIdAllocation {
  readonly id: number;
  readonly nextId: number;
}

export function assertAuthoritativeInteger(
  value: unknown,
  label: string,
  minimum = Number.MIN_SAFE_INTEGER,
  maximum = Number.MAX_SAFE_INTEGER,
): number {
  if (Object.is(value, -0)) {
    throw new RangeError(`${label} cannot be negative zero`);
  }
  if (!Number.isSafeInteger(value)) {
    throw new RangeError(`${label} must be a safe integer`);
  }
  const integer = value as number;
  if (integer < minimum || integer > maximum) {
    throw new RangeError(`${label} is outside the supported range`);
  }
  return integer;
}

export function assertUint32(value: unknown, label: string): number {
  try {
    return assertAuthoritativeInteger(value, label, 0, UINT32_MAX);
  } catch (error) {
    throw new RangeError(`${label} must be a uint32`, { cause: error });
  }
}

export function assertFixedPointXZ(value: unknown): Readonly<FixedPointXZ> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new TypeError("fixed-point XZ must be a plain record");
  }
  const record = value as Record<string, unknown>;
  return Object.freeze({
    x: assertAuthoritativeInteger(record.x, "fixed-point x"),
    z: assertAuthoritativeInteger(record.z, "fixed-point z"),
  });
}

export function allocateRunScopedId(nextId: unknown): Readonly<RunScopedIdAllocation> {
  const id = assertAuthoritativeInteger(nextId, "next run-scoped id", 1);
  if (id === Number.MAX_SAFE_INTEGER) {
    throw new RangeError("run-scoped id sequence is exhausted");
  }
  return Object.freeze({ id, nextId: id + 1 });
}

export function renderRunScopedId(id: unknown): string {
  return assertAuthoritativeInteger(id, "run-scoped id", 1).toString(10);
}
